import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Award, Users, Target, ArrowLeft, Rocket, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const milestones = [
  {
    year: '2000',
    title: 'O começo',
    text: 'Abertura da empresa ArchiteCAD, no Rio de Janeiro, em parceria com a arquiteta Alessandra Bernstein.',
  },
  {
    year: '2005',
    title: 'Pioneirismo em Revit',
    text: 'Lançamento do primeiro livro de AutoCAD de autoria de Alexander Justi. Treinamento oficial da Autodesk nos EUA (Disney) para mais de 2.000 técnicos em Revit. De volta ao Brasil, escreve e lança o primeiro livro de Revit do mercado brasileiro: "Revit Building 9.0".',
  },
  {
    year: '2007',
    title: 'Cursos oficiais de Revit',
    text: 'Lançamento do seu último livro sobre AutoCAD e dos primeiros cursos oficiais de Revit pela ArchiteCAD.',
  },
  {
    year: '2010',
    title: 'Nasce o Grupo AJ',
    text: 'Livro "Revit Architecture 2010". Venda da ArchiteCAD e mudança para Brasília. Abertura do Grupo AJ com a engenheira Edméa Carmo e lançamento do Curso Master em Revit, com turmas em Brasília, Chapecó, Rio de Janeiro, Alagoas, Belém e outras capitais.',
  },
  {
    year: '2018',
    title: 'Uma grande perda',
    text: 'A engenheira Edméa Carmo, esposa do diretor Alexander Justi, parte vítima de um câncer — um impacto profundo na empresa e na família. A gestão passa a ser conduzida pelos sócios.',
    tone: 'hard',
  },
  {
    year: '2020',
    title: 'Era online',
    text: 'Com a pandemia, as turmas passam a ser oferecidas integralmente no formato on-line, ampliando o alcance por todo o Brasil.',
  },
  {
    year: '2025',
    title: 'Recomeço',
    text: 'Rompimento societário e abertura da AJ Consultoria e Gestão em BIM. Um ano duríssimo: o AVC do diretor Alexander, o falecimento de seu pai no mês seguinte e o fim da antiga sociedade.',
    tone: 'hard',
  },
  {
    year: '2026',
    title: 'Reboot: nasce o BIMPORTAL',
    text: 'Um verdadeiro reboot profissional com o lançamento do BIMPORTAL e das marcas BIMACADEMY, BIMCONNECT, BIMSERVIÇOS, BIMPROJETOS e BIMTALKS. O Master em Revit evolui para a Formação Revit Expert (320h) — a maior e mais completa formação profissional em Revit em língua portuguesa. Mais de 12.000 alunos formados e uma autoridade consolidada no mercado.',
    highlight: true,
  },
];

const values = [
  { icon: Heart, title: 'Amor pelo que faz', text: 'Paixão genuína por BIM e por ensinar — é o que sustenta cada projeto e cada aluno transformado.' },
  { icon: Target, title: 'Excelência técnica', text: 'Metodologia testada e atualizada com as melhores práticas BIM do mercado.' },
  { icon: Users, title: 'Comunidade', text: 'Uma rede ativa de profissionais, alunos e empresas que crescem juntos.' },
  { icon: Rocket, title: 'Inovação', text: 'Sempre à frente, trazendo tecnologia e novas soluções para a construção.' },
];

const SobreEmpresaPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black" data-testid="sobre-empresa-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-600/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-800/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8 text-sm"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} className="mr-2" /> Voltar para a Home
          </button>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 mb-6">
              <Building2 size={15} className="text-red-500" /> Sobre a Empresa
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-6">
              <img
                src="/logos/aj-solucoes.png"
                alt="AJ Soluções BIM"
                className="h-24 md:h-28 w-auto object-contain shrink-0 drop-shadow-lg"
              />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
                AJ Consultoria e Gestão em <span className="text-red-600">BIM</span> Ltda.
              </h1>
            </div>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Pioneira na formação BIM no Brasil, a AJ nasceu com a missão de transformar a forma como
              profissionais e empresas projetam, constroem e gerenciam. Desde 2005, somos referência em
              educação, consultoria e projetos BIM na América Latina.
            </p>
          </div>
        </div>
      </section>

      {/* Roadmap / Linha do tempo */}
      <section className="py-20 border-t border-zinc-800">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-16 text-center">
            Nossa <span className="text-red-600">trajetória</span>
          </h2>

          <div className="relative max-w-4xl mx-auto">
            {/* Linha vertical do roadmap */}
            <div className="absolute left-5 md:left-1/2 top-2 bottom-2 w-1 bg-gradient-to-b from-red-600 via-red-700 to-red-900/30 md:-translate-x-1/2 rounded-full" />

            <div className="space-y-12">
              {milestones.map((m, i) => {
                const left = i % 2 === 0;
                const isLast = i === milestones.length - 1 || m.highlight;
                const isHard = m.tone === 'hard';
                const nodeClass = isLast
                  ? 'bg-red-500 animate-pulse shadow-[0_0_18px_rgba(239,68,68,0.8)]'
                  : isHard
                    ? 'bg-zinc-500'
                    : 'bg-red-600';
                return (
                  <div
                    key={m.year}
                    className="relative md:grid md:grid-cols-2 md:gap-12 md:items-center"
                    data-testid={`milestone-${m.year}`}
                  >
                    {/* Nó na linha */}
                    <div className="absolute left-5 md:left-1/2 -translate-x-1/2 top-6 md:top-1/2 md:-translate-y-1/2 z-10">
                      <div className={`w-5 h-5 rounded-full ring-4 ring-black ${nodeClass}`} />
                    </div>

                    {/* Card */}
                    <div
                      className={`pl-14 md:pl-0 ${
                        left
                          ? 'md:col-start-1 md:pr-12 md:text-right'
                          : 'md:col-start-2 md:pl-12'
                      }`}
                    >
                      <div className={`inline-block w-full bg-zinc-900/60 border rounded-2xl p-6 transition-all duration-300 ${isLast ? 'border-red-600/50 hover:border-red-500' : isHard ? 'border-zinc-700 hover:border-zinc-500' : 'border-zinc-800 hover:border-red-600/50'}`}>
                        <span className={`inline-block font-black text-2xl ${isLast ? 'text-red-500' : isHard ? 'text-gray-300' : 'text-red-600'}`}>
                          {m.year}
                        </span>
                        <h3 className="text-white font-semibold text-lg mt-1 mb-2">{m.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{m.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-12 text-center">
            O que nos <span className="text-red-600">move</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-7 text-center">
                  <div className="w-14 h-14 rounded-xl bg-red-600/15 flex items-center justify-center mx-auto mb-4">
                    <Icon size={26} className="text-red-500" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{v.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mensagem de encerramento */}
      <section className="py-20 border-t border-zinc-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto relative">
            <span className="absolute -top-8 -left-2 text-red-600/30 text-8xl font-serif leading-none select-none">“</span>
            <div className="relative space-y-5 text-gray-300 text-lg md:text-xl leading-relaxed">
              <p>
                O que move um profissional ou a sua empresa é <span className="text-white font-semibold">nunca desistir</span>.
                Focar em algo e sempre seguir em frente, mesmo que seja obrigado a dar passos para trás.
                É a sua resiliência, mesmo com tantos tropeços e problemas durante a trajetória.
              </p>
              <p className="text-white italic border-l-2 border-red-600 pl-5">
                Há um velho ditado que diz: é mais fácil dar um impulso para sair de um buraco quando você está no fundo.
              </p>
              <p>
                Existem pessoas que apenas passaram pela sua vida e pessoas que simplesmente ficaram — mesmo que em memória.
                A nova empresa vai muito além e mais longe do que muitos tentaram ou tentam até hoje.
              </p>
              <p>
                É a capacidade de se <span className="text-red-500 font-semibold">reinventar</span> e criar algo muito maior
                do que se havia pensado originalmente, tornando-se uma das maiores empresas de BIM do mundo.
              </p>
            </div>
            <div className="relative mt-8 text-right">
              <p className="text-white font-bold text-lg">Alexander Justi</p>
              <p className="text-red-500 text-sm">Fundador • AJ Soluções BIM</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <Award size={36} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Quer fazer parte dessa história?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Conheça nossas formações, consultoria e projetos e leve o BIM para o próximo nível.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/academy')}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cta-academy-btn"
            >
              Ver formações
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-zinc-600 text-gray-200 hover:bg-white/5"
              data-testid="cta-home-btn"
            >
              Voltar para a Home
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobreEmpresaPage;
